import React, { useState } from 'react';
import {
    Box,
    VStack,
    Text,
    Button,
    HStack,
    Badge,
    Center,
    Icon,
    IconButton,
    Collapse,
    Grid,
    GridItem,
    StackDivider,
    SimpleGrid,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
} from '@chakra-ui/react';
import { AiOutlineClose } from 'react-icons/ai';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { TemplateDetailViewProps } from './templateDetailProps';
import { ReportingShellViewBaseConfig } from '../../../base';
import { VAROS_YELLOW } from '../../../../../../config';

export function createTemplateDetailView(
    config: ReportingShellViewBaseConfig
): React.FC<TemplateDetailViewProps> {
    const {
        router: {
            insert: {
                Component: { BackButton },
            },
        },
    } = config;

    const DeliverableItem: React.FC<{
        deliverable: {
            id: string;
            title: string;
            description: string;
        };
    }> = ({ deliverable }) => {
        const [isExpanded, setIsExpanded] = useState(false);

        return (
            <>
                <HStack
                    align="center"
                    spacing={3}
                    w="full"
                    cursor="pointer"
                    onClick={() => setIsExpanded(!isExpanded)}
                    px={4}
                    h={12}
                    color="whiteAlpha.800"
                    userSelect="none"
                    _hover={{
                        bg: 'whiteAlpha.50',
                    }}
                >
                    <Icon
                        as={isExpanded ? ChevronDownIcon : ChevronRightIcon}
                        color="whiteAlpha.400"
                        fontSize="lg"
                    />
                    <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="whiteAlpha.600"
                        noOfLines={1}
                    >
                        {deliverable.title}
                    </Text>
                </HStack>
                <Collapse in={isExpanded} animateOpacity={false}>
                    <Box px={4} py={3} borderTopWidth={2} borderColor="whiteAlpha.100">
                        <Text fontSize="sm" color="whiteAlpha.700">
                            {deliverable.description}
                        </Text>
                    </Box>
                </Collapse>
            </>
        );
    };

    const ProcessItem: React.FC<{
        step: {
            id: string;
            title: string;
            description: string;
            duration: string;
        };
    }> = ({ step }) => {
        const [isExpanded, setIsExpanded] = useState(false);

        return (
            <>
                <HStack
                    align="center"
                    justify="space-between"
                    w="full"
                    cursor="pointer"
                    onClick={() => setIsExpanded(!isExpanded)}
                    px={4}
                    h={12}
                    color="whiteAlpha.800"
                    userSelect="none"
                    _hover={{
                        bg: 'whiteAlpha.50',
                    }}
                >
                    <HStack spacing={3} flex="1" minW={0}>
                        <Icon
                            as={isExpanded ? ChevronDownIcon : ChevronRightIcon}
                            color="whiteAlpha.400"
                            fontSize="lg"
                        />
                        <Text
                            fontSize="sm"
                            fontWeight="semibold"
                            color="whiteAlpha.600"
                            noOfLines={1}
                            flex="1"
                        >
                            {step.title}
                        </Text>
                    </HStack>
                    <Text
                        fontSize="xs"
                        fontWeight="medium"
                        color="whiteAlpha.500"
                        flexShrink={0}
                    >
                        {step.duration}
                    </Text>
                </HStack>
                <Collapse in={isExpanded} animateOpacity={false}>
                    <Box px={4} py={3} borderTopWidth={2} borderColor="whiteAlpha.100">
                        <Text fontSize="sm" color="whiteAlpha.700">
                            {step.description}
                        </Text>
                    </Box>
                </Collapse>
            </>
        );
    };

    return (props) => {
        return (
            <Grid
                position="absolute"
                left={0}
                top={0}
                right={0}
                bottom={0}
                rowGap={2}
                gridTemplateRows="min-content 1fr min-content"
            >
                {/* Header with back button - Top level */}
                <GridItem
                    w="full"
                    px={4}
                    py={3}
                    // borderBottomWidth={1}
                    // borderColor="whiteAlpha.200"
                >
                    <SimpleGrid w="full" columns={3} templateColumns="1fr 4fr 1fr">
                        <HStack>
                            <BackButton
                                aria-label="Back"
                                as={IconButton}
                                icon={<Icon as={ChevronLeftIcon} fontSize="xl" />}
                                size="sm"
                                bg="none"
                                color="whiteAlpha.600"
                                iconSpacing={3}
                                _hover={{
                                    bg: 'whiteAlpha.100',
                                }}
                                _focus={{ outline: 'none' }}
                            />
                        </HStack>
                        <HStack
                            justify="center"
                            fontWeight="semibold"
                            fontSize="md"
                            userSelect="none"
                            color="whiteAlpha.800"
                        >
                            <Text>Solution</Text>
                        </HStack>
                        <HStack justify="end">
                            {props.overlay && (
                                <IconButton
                                    {...props.overlay.close.button}
                                    aria-label="Close"
                                    icon={<AiOutlineClose />}
                                    size="sm"
                                    bg="none"
                                    color="whiteAlpha.600"
                                    _focus={{ outline: 'none' }}
                                    _hover={{
                                        bg: 'whiteAlpha.100',
                                    }}
                                />
                            )}
                        </HStack>
                    </SimpleGrid>
                </GridItem>

                {/* Content area - Scrollable */}
                <GridItem
                    overflowY="auto"
                    minH={0}
                    sx={{
                        scrollbarGutter: 'stable',
                        '&::-webkit-scrollbar': {
                            '-webkit-appearance': 'none',
                            width: '0.5rem',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                        },
                        '&::-webkit-scrollbar-corner': {
                            '-webkit-appearance': 'none',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            borderRadius: '1rem',
                            background: 'rgba(255,255,255,0.3)',
                        },
                    }}
                >
                    <Box px={4} pb={6}>
                        <VStack w="full" spacing={6} align="start">
                            {/* Template Header */}
                            <VStack
                                align="start"
                                spacing={4}
                                w="full"
                                divider={
                                    <StackDivider
                                        borderWidth={1}
                                        borderColor="whiteAlpha.100"
                                    />
                                }
                            >
                                <HStack spacing={4} align="start">
                                    <Center
                                        w={16}
                                        h={16}
                                        bg="whiteAlpha.100"
                                        borderRadius="md"
                                        flexShrink={0}
                                    >
                                        {props.template.icon.kind === 'emoji' ? (
                                            <Text fontSize="2xl">
                                                {props.template.icon.value}
                                            </Text>
                                        ) : (
                                            <Icon
                                                as={props.template.icon.Icon}
                                                fontSize="2xl"
                                            />
                                        )}
                                    </Center>
                                    <VStack align="start" spacing={2} flex={1}>
                                        <VStack align="start" spacing={-1}>
                                            <Text
                                                fontSize="lg"
                                                fontWeight="semibold"
                                                color="whiteAlpha.900"
                                                noOfLines={2}
                                            >
                                                {props.template.label}
                                            </Text>
                                            <Text
                                                fontSize="sm"
                                                color="whiteAlpha.500"
                                                fontWeight="medium"
                                                noOfLines={1}
                                            >
                                                {props.template.caption}
                                            </Text>
                                        </VStack>
                                        <Button
                                            {...props.install.button}
                                            size="xs"
                                            px={5}
                                            borderRadius="full"
                                            colorScheme="blue"
                                            bg="blue.500"
                                            color="whiteAlpha.900"
                                            fontWeight="semibold"
                                            iconSpacing={1}
                                            textTransform="uppercase"
                                            _hover={{
                                                bg: 'blue.600',
                                            }}
                                            _active={{
                                                bg: 'blue.700',
                                            }}
                                        >
                                            {props.install.label.short}
                                        </Button>
                                    </VStack>
                                </HStack>

                                <Text fontSize="sm" color="whiteAlpha.700">
                                    {props.config.overview}
                                </Text>
                            </VStack>

                            {/* Deliverables and Process Tabs */}
                            <VStack align="start" spacing={3} w="full">
                                <Tabs w="full" variant="soft-rounded" isFitted={true}>
                                    <TabList as={HStack} spacing={2}>
                                        <Tab
                                            h={8}
                                            bg="none"
                                            color="whiteAlpha.500"
                                            _hover={{ bg: 'whiteAlpha.50' }}
                                            _selected={{
                                                bg: 'whiteAlpha.100',
                                                color: 'whiteAlpha.800',
                                            }}
                                        >
                                            {props.config.deliverables.label}
                                        </Tab>
                                        <Tab
                                            h={8}
                                            bg="none"
                                            color="whiteAlpha.500"
                                            _hover={{ bg: 'whiteAlpha.50' }}
                                            _selected={{
                                                bg: 'whiteAlpha.100',
                                                color: 'whiteAlpha.800',
                                            }}
                                        >
                                            {props.config.process.label}
                                        </Tab>
                                    </TabList>
                                    <TabPanels>
                                        <TabPanel px={0} pt={3}>
                                            <Box
                                                bg="whiteAlpha.30"
                                                borderRadius="lg"
                                                borderWidth={2}
                                                borderColor="whiteAlpha.100"
                                                w="full"
                                                overflow="hidden"
                                            >
                                                <VStack
                                                    spacing={0}
                                                    w="full"
                                                    divider={
                                                        <StackDivider
                                                            borderWidth={1}
                                                            borderColor="whiteAlpha.100"
                                                        />
                                                    }
                                                >
                                                    {props.config.deliverables.items.map(
                                                        (deliverable) => (
                                                            <DeliverableItem
                                                                key={deliverable.id}
                                                                deliverable={deliverable}
                                                            />
                                                        )
                                                    )}
                                                </VStack>
                                            </Box>
                                        </TabPanel>
                                        <TabPanel px={0} pt={3}>
                                            <Box
                                                bg="whiteAlpha.30"
                                                borderRadius="lg"
                                                borderWidth={2}
                                                borderColor="whiteAlpha.100"
                                                w="full"
                                                overflow="hidden"
                                            >
                                                <VStack
                                                    spacing={0}
                                                    w="full"
                                                    divider={
                                                        <StackDivider
                                                            borderWidth={1}
                                                            borderColor="whiteAlpha.100"
                                                        />
                                                    }
                                                >
                                                    {props.config.process.items.map(
                                                        (step) => (
                                                            <ProcessItem
                                                                key={step.id}
                                                                step={step}
                                                            />
                                                        )
                                                    )}
                                                </VStack>
                                            </Box>
                                        </TabPanel>
                                    </TabPanels>
                                </Tabs>
                            </VStack>

                            {/* Metadata Section */}
                            <VStack align="start" spacing={3} w="full">
                                <Text
                                    fontSize="lg"
                                    fontWeight="bold"
                                    color="whiteAlpha.900"
                                    letterSpacing="tight"
                                >
                                    {props.keywords.label}
                                </Text>
                                <Box
                                    bg="whiteAlpha.30"
                                    borderRadius="lg"
                                    borderWidth={2}
                                    borderColor="whiteAlpha.100"
                                    w="full"
                                    overflow="hidden"
                                >
                                    <VStack
                                        spacing={0}
                                        w="full"
                                        divider={
                                            <StackDivider
                                                borderWidth={1}
                                                borderColor="whiteAlpha.100"
                                            />
                                        }
                                    >
                                        {props.keywords.items.map((keyword, index) => (
                                            <HStack
                                                key={keyword.id}
                                                justify="space-between"
                                                align="center"
                                                w="full"
                                                px={4}
                                                h={12}
                                                color="whiteAlpha.800"
                                            >
                                                <Text
                                                    fontSize="xs"
                                                    userSelect="none"
                                                    color="whiteAlpha.400"
                                                    textTransform="uppercase"
                                                    fontWeight="bold"
                                                    letterSpacing="wider"
                                                >
                                                    {keyword.label}
                                                </Text>
                                                {keyword.value.kind === 'badge' ? (
                                                    <Badge
                                                        colorScheme={
                                                            keyword.value.colorScheme
                                                        }
                                                        variant="subtle"
                                                        fontSize="xs"
                                                        fontWeight="semibold"
                                                        px={2}
                                                        py={1}
                                                        borderRadius="md"
                                                        display="flex"
                                                        alignItems="center"
                                                    >
                                                        {keyword.value.text}
                                                    </Badge>
                                                ) : (
                                                    <Text
                                                        fontSize="sm"
                                                        fontWeight="semibold"
                                                        display="flex"
                                                        alignItems="center"
                                                    >
                                                        {keyword.value.text}
                                                    </Text>
                                                )}
                                            </HStack>
                                        ))}
                                    </VStack>
                                </Box>
                            </VStack>
                        </VStack>
                    </Box>
                </GridItem>

                {/* Sticky Action Button */}
                <GridItem p={4} borderTopWidth={2} borderColor="whiteAlpha.200">
                    <Button
                        {...props.install.button}
                        borderRadius="full"
                        w="full"
                        colorScheme="blue"
                        // bg="blue.500"
                        // color="whiteAlpha.900"
                        bg="whiteAlpha.900"
                        color="blackAlpha.900"
                        // fontWeight="bold"
                        // fontSize="xs"
                        iconSpacing={1}
                        // letterSpacing="wide"
                        // textTransform="uppercase"
                        _hover={{
                            // bg: 'blue.600',
                            bg: 'whiteAlpha.800',
                        }}
                        _active={{
                            bg: 'whiteAlpha.700',
                            // bg: 'blue.700',
                        }}
                    >
                        {props.install.label.full}
                    </Button>
                </GridItem>
            </Grid>
        );
    };
}
